import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Check, Copy, FileText, Plus, Save, Trash2, Upload, X } from 'lucide-react';
import { Btn, IconButton } from '../ui';
import { Select, TextField } from '../forms';
import { focusableWithin, wrapTabFocus } from '../../lib/focusTrap';
import {
  createEmptyVaultHook,
  createEmptyVaultMarketingAsset,
  createEmptyVaultMetadata,
  createEmptyVaultPack,
  createEmptyVaultPreset,
  createEmptyVaultPrompt,
  createEmptyVaultStructure,
  createVaultPackJsonTemplate,
  formatVaultPackJson,
  normalizeVaultPack,
  parseVaultPackJson,
  readVaultPackImportFile,
} from '../../lib/vaultPackImport';
import { c } from '../../styles/theme';
import type {
  VaultHook,
  VaultMarketingAsset,
  VaultMetadata,
  VaultPack,
  VaultPreset,
  VaultPrompt,
  VaultStructure,
} from '../../types/domain';

const FIELD_STYLE = { padding: '8px 10px' } as const;
const CUSTOM_OPTION = '__custom__';
const SECTION_KEYS = [
  'details',
  'prompts',
  'presets',
  'structures',
  'hooks',
  'metadata',
  'marketing',
] as const;

type VaultArrayKey = 'prompts' | 'presets' | 'structures' | 'hooks' | 'metadata' | 'marketing';
type SectionKey = (typeof SECTION_KEYS)[number];
type EditableRecord = Record<string, unknown>;
type VaultArrayItem = VaultPrompt | VaultPreset | VaultStructure | VaultHook | VaultMetadata | VaultMarketingAsset;
type OptionSetKey = 'categories' | 'genres' | 'moods' | 'difficulties' | 'platforms';

export interface AdminVaultFieldOptions {
  categories: string[];
  genres: string[];
  moods: string[];
  difficulties: string[];
  platforms: string[];
}

type ArrayFieldConfig = {
  key: string;
  label: string;
  multiline?: boolean;
  rows?: number;
  readOnly?: boolean;
  placeholder?: string;
  parse?: (value: string) => unknown;
  format?: (value: unknown) => string;
  optionsKey?: OptionSetKey;
};

type ArraySectionConfig = {
  title: string;
  singular: string;
  createEmpty: () => VaultArrayItem;
  fields: readonly ArrayFieldConfig[];
};

const ARRAY_SECTIONS: Record<VaultArrayKey, ArraySectionConfig> = {
  prompts: {
    title: 'Prompts',
    singular: 'Prompt',
    createEmpty: () => createEmptyVaultPrompt(),
    fields: [
      { key: 'id', label: 'ID', readOnly: true },
      { key: 'title', label: 'Title' },
      { key: 'genre', label: 'Genre', optionsKey: 'genres' },
      { key: 'mood', label: 'Mood', optionsKey: 'moods' },
      { key: 'difficulty', label: 'Difficulty', optionsKey: 'difficulties' },
      { key: 'text', label: 'Text', multiline: true, rows: 5 },
    ],
  },
  presets: {
    title: 'Presets',
    singular: 'Preset',
    createEmpty: () => createEmptyVaultPreset(),
    fields: [
      { key: 'id', label: 'ID', readOnly: true },
      { key: 'name', label: 'Name' },
      { key: 'tempo', label: 'Tempo' },
      { key: 'mood', label: 'Mood', optionsKey: 'moods' },
      {
        key: 'energy',
        label: 'Energy',
        placeholder: '0-100',
        parse: (value) => {
          if (!value.trim()) return 0;
          const next = Number(value);
          return Number.isFinite(next) ? next : 0;
        },
        format: (value) => String(value ?? 0),
      },
      { key: 'vocals', label: 'Vocals' },
      { key: 'structure', label: 'Structure' },
    ],
  },
  structures: {
    title: 'Structures',
    singular: 'Structure',
    createEmpty: () => createEmptyVaultStructure(),
    fields: [
      { key: 'id', label: 'ID', readOnly: true },
      { key: 'name', label: 'Name' },
      { key: 'layout', label: 'Layout', multiline: true, rows: 4 },
    ],
  },
  hooks: {
    title: 'Hooks',
    singular: 'Hook',
    createEmpty: () => createEmptyVaultHook(),
    fields: [
      { key: 'id', label: 'ID', readOnly: true },
      { key: 'text', label: 'Text', multiline: true, rows: 4 },
      { key: 'genre', label: 'Genre', optionsKey: 'genres' },
    ],
  },
  metadata: {
    title: 'Metadata',
    singular: 'Metadata item',
    createEmpty: () => createEmptyVaultMetadata(),
    fields: [
      { key: 'id', label: 'ID', readOnly: true },
      { key: 'platform', label: 'Platform', optionsKey: 'platforms' },
      { key: 'title', label: 'Title' },
      { key: 'text', label: 'Text', multiline: true, rows: 5 },
    ],
  },
  marketing: {
    title: 'Marketing',
    singular: 'Marketing item',
    createEmpty: () => createEmptyVaultMarketingAsset(),
    fields: [
      { key: 'id', label: 'ID', readOnly: true },
      { key: 'platform', label: 'Platform', optionsKey: 'platforms' },
      { key: 'title', label: 'Title' },
      { key: 'text', label: 'Text', multiline: true, rows: 5 },
    ],
  },
};

export interface AdminVaultPackEditorProps {
  mode: 'create' | 'edit';
  pack: VaultPack | null;
  categories: string[];
  fieldOptions: AdminVaultFieldOptions;
  pending: boolean;
  onClose: () => void;
  onSave: (pack: VaultPack) => Promise<void>;
  onDelete?: (id: string) => Promise<void>;
}

function buildInitialDraft(pack: VaultPack | null): VaultPack {
  return pack ? normalizeVaultPack(pack, pack) : createEmptyVaultPack();
}

function isArraySection(key: SectionKey | null): key is VaultArrayKey {
  return Boolean(key) && key !== 'details';
}

function itemsFor<K extends VaultArrayKey>(pack: VaultPack, key: K): VaultPack[K] {
  return pack[key];
}

function upsertItem<K extends VaultArrayKey>(
  pack: VaultPack,
  key: K,
  index: number,
  item: VaultPack[K][number]
): VaultPack {
  const current = itemsFor(pack, key);
  const next =
    index < current.length
      ? current.map((candidate, candidateIndex) => (candidateIndex === index ? item : candidate))
      : [...current, item];
  return { ...pack, [key]: next };
}

function removeItem<K extends VaultArrayKey>(pack: VaultPack, key: K, index: number): VaultPack {
  return {
    ...pack,
    [key]: itemsFor(pack, key).filter((_, candidateIndex) => candidateIndex !== index),
  };
}

function fieldValue(item: EditableRecord, field: ArrayFieldConfig): string {
  if (field.format) return field.format(item[field.key]);
  const value = item[field.key];
  if (value === undefined || value === null) return '';
  return String(value);
}

function patchItem(item: VaultArrayItem, field: ArrayFieldConfig, value: string): VaultArrayItem {
  return {
    ...item,
    [field.key]: field.parse ? field.parse(value) : value,
  };
}

function asEditableRecord(item: VaultArrayItem): EditableRecord {
  return item as unknown as EditableRecord;
}

function hasMeaningfulValues(sectionKey: VaultArrayKey, item: VaultArrayItem): boolean {
  const record = asEditableRecord(item);
  return ARRAY_SECTIONS[sectionKey].fields.some(
    (field) => !field.readOnly && fieldValue(record, field).trim()
  );
}

export default function AdminVaultPackEditor({
  mode,
  pack,
  categories,
  fieldOptions,
  pending,
  onClose,
  onSave,
  onDelete,
}: AdminVaultPackEditorProps) {
  const initialDraftRef = useRef<VaultPack | null>(null);
  if (!initialDraftRef.current) initialDraftRef.current = buildInitialDraft(pack);

  const [draft, setDraft] = useState<VaultPack>(initialDraftRef.current);
  const [jsonText, setJsonText] = useState(() => formatVaultPackJson(initialDraftRef.current!));
  const [error, setError] = useState('');
  const [copiedTemplate, setCopiedTemplate] = useState(false);
  const [activeSectionIndex, setActiveSectionIndex] = useState(0);
  const [activeItemIndex, setActiveItemIndex] = useState(0);
  const [itemDraft, setItemDraft] = useState<VaultArrayItem | null>(null);
  const [sectionItemIndices, setSectionItemIndices] = useState<Partial<Record<VaultArrayKey, number>>>({});
  const [customFields, setCustomFields] = useState<Record<string, boolean>>({});
  const rootRef = useRef<HTMLDivElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);
  const headingId = useId();

  const activeSectionKey =
    activeSectionIndex < SECTION_KEYS.length ? SECTION_KEYS[activeSectionIndex] : null;
  const activeArraySection =
    isArraySection(activeSectionKey) ? ARRAY_SECTIONS[activeSectionKey] : null;

  useEffect(() => {
    const next = buildInitialDraft(pack);
    setDraft(next);
    setJsonText(formatVaultPackJson(next));
    setError('');
    setCopiedTemplate(false);
    setActiveSectionIndex(0);
    setActiveItemIndex(0);
    setItemDraft(null);
    setSectionItemIndices({});
    setCustomFields({});
  }, [pack, mode]);

  useEffect(() => {
    if (!activeArraySection || !isArraySection(activeSectionKey)) {
      setItemDraft(null);
      return;
    }
    const current = itemsFor(draft, activeSectionKey)[activeItemIndex];
    setItemDraft(current ?? activeArraySection.createEmpty());
  }, [draft, activeArraySection, activeSectionKey, activeItemIndex]);

  useEffect(() => {
    setJsonText(formatVaultPackJson(draft));
  }, [draft]);

  useEffect(() => {
    if (!copiedTemplate) return;
    const timeout = window.setTimeout(() => setCopiedTemplate(false), 1800);
    return () => window.clearTimeout(timeout);
  }, [copiedTemplate]);

  useEffect(() => {
    previouslyFocused.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const root = rootRef.current;
    if (root) focusableWithin(root)[0]?.focus();
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKeyDown = (e: KeyboardEvent) => {
      const current = rootRef.current;
      if (!current) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key !== 'Tab') return;
      wrapTabFocus(current, e);
    };

    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      document.body.style.overflow = previousOverflow;
      const target = previouslyFocused.current;
      previouslyFocused.current = null;
      if (target?.isConnected) target.focus();
    };
  }, [onClose]);

  const summary = useMemo(
    () => ({
      prompts: draft.prompts.length,
      presets: draft.presets.length,
      structures: draft.structures.length,
      hooks: draft.hooks.length,
      metadata: draft.metadata.length,
      marketing: draft.marketing.length,
    }),
    [draft]
  );

  const currentArrayLength =
    activeSectionKey && isArraySection(activeSectionKey)
      ? itemsFor(draft, activeSectionKey).length
      : 0;
  const editingExistingItem = Boolean(
    activeSectionKey && isArraySection(activeSectionKey) && activeItemIndex < currentArrayLength
  );
  const canGoPrevious =
    activeSectionKey === null ||
    activeSectionIndex > 0 ||
    (isArraySection(activeSectionKey) && activeItemIndex > 0);

  function patch(next: Partial<VaultPack>) {
    setDraft((current) => ({ ...current, ...next }));
    setError('');
    setCopiedTemplate(false);
  }

  function applyJson(text: string, reassignIds = false) {
    const parsed = parseVaultPackJson(text, draft, {
      reassignIds,
      preserveCurrentPackId: mode === 'edit',
    });
    setDraft(parsed);
    setError('');
    setCopiedTemplate(false);
    setActiveSectionIndex(0);
    setActiveItemIndex(0);
    setItemDraft(null);
    setSectionItemIndices({});
    setCustomFields({});
  }

  function setSectionCursor(sectionKey: VaultArrayKey, index: number) {
    setSectionItemIndices((current) => ({ ...current, [sectionKey]: index }));
  }

  function openSection(sectionIndex: number, itemIndex = 0) {
    setActiveSectionIndex(sectionIndex);
    setActiveItemIndex(itemIndex);
    setItemDraft(null);
  }

  function optionValuesFor(key?: OptionSetKey): string[] {
    if (!key) return [];
    return fieldOptions[key];
  }

  function customFieldPath(fieldKey: string): string {
    if (activeSectionKey === 'details') return `details.${fieldKey}`;
    if (!activeSectionKey) return fieldKey;
    return `${activeSectionKey}.${activeItemIndex}.${fieldKey}`;
  }

  function isCustomField(path: string, value: string, options: string[]): boolean {
    return customFields[path] || (value.trim() !== '' && !options.includes(value));
  }

  function updateCustomField(path: string, enabled: boolean) {
    setCustomFields((current) => ({ ...current, [path]: enabled }));
  }

  function commitCurrentArrayItem(requireValue: boolean): boolean {
    if (!activeSectionKey || !isArraySection(activeSectionKey) || !itemDraft) return true;
    const canSave = editingExistingItem || hasMeaningfulValues(activeSectionKey, itemDraft);
    if (requireValue && !canSave) {
      setError(
        `Add at least one ${ARRAY_SECTIONS[activeSectionKey].singular.toLowerCase()} field or skip this section.`
      );
      return false;
    }
    if (!canSave) return true;
    setDraft((current) => upsertItem(current, activeSectionKey, activeItemIndex, itemDraft as never));
    return true;
  }

  function patchArrayDraft(field: ArrayFieldConfig, value: string) {
    if (!activeSectionKey || !isArraySection(activeSectionKey) || !itemDraft) return;
    const updated = patchItem(itemDraft, field, value);
    setItemDraft(updated);
    if (editingExistingItem || hasMeaningfulValues(activeSectionKey, updated)) {
      setDraft((current) => upsertItem(current, activeSectionKey, activeItemIndex, updated as never));
    }
    setError('');
  }

  function handleNext() {
    if (!activeSectionKey) return;
    if (activeSectionKey === 'details') {
      openSection(activeSectionIndex + 1, sectionItemIndices.prompts ?? 0);
      setError('');
      return;
    }
    if (!commitCurrentArrayItem(true)) return;
    const nextIndex = activeItemIndex + 1;
    setSectionCursor(activeSectionKey, nextIndex);
    setActiveItemIndex(nextIndex);
    setError('');
  }

  function handleSkip() {
    if (!activeSectionKey || !isArraySection(activeSectionKey)) return;
    const shouldAdvanceCursor = itemDraft ? editingExistingItem || hasMeaningfulValues(activeSectionKey, itemDraft) : false;
    if (!commitCurrentArrayItem(false)) return;
    setSectionCursor(activeSectionKey, shouldAdvanceCursor ? activeItemIndex + 1 : activeItemIndex);
    openSection(activeSectionIndex + 1, 0);
    setError('');
  }

  function handlePrevious() {
    if (!canGoPrevious) return;

    if (!activeSectionKey) {
      const previousSectionIndex = SECTION_KEYS.length - 1;
      const previousSectionKey = SECTION_KEYS[previousSectionIndex];
      const previousItemIndex = isArraySection(previousSectionKey)
        ? sectionItemIndices[previousSectionKey] ?? itemsFor(draft, previousSectionKey).length
        : 0;
      openSection(previousSectionIndex, previousItemIndex);
      setError('');
      return;
    }

    if (activeSectionKey !== 'details' && !commitCurrentArrayItem(false)) return;

    if (isArraySection(activeSectionKey) && activeItemIndex > 0) {
      const previousItemIndex = activeItemIndex - 1;
      setSectionCursor(activeSectionKey, previousItemIndex);
      setActiveItemIndex(previousItemIndex);
      setError('');
      return;
    }

    const previousSectionIndex = Math.max(0, activeSectionIndex - 1);
    const previousSectionKey = SECTION_KEYS[previousSectionIndex];
    const previousItemIndex = isArraySection(previousSectionKey)
      ? sectionItemIndices[previousSectionKey] ?? itemsFor(draft, previousSectionKey).length
      : 0;
    openSection(previousSectionIndex, previousItemIndex);
    setError('');
  }

  function handleRemoveCurrentItem() {
    if (!activeSectionKey || !isArraySection(activeSectionKey) || !editingExistingItem) return;
    const nextDraft = removeItem(draft, activeSectionKey, activeItemIndex);
    const nextLength = itemsFor(nextDraft, activeSectionKey).length;
    const nextIndex = activeItemIndex >= nextLength ? nextLength : activeItemIndex;
    setDraft(nextDraft);
    setSectionCursor(activeSectionKey, nextIndex);
    setActiveItemIndex(nextIndex);
    setError('');
  }

  async function handleSave() {
    try {
      const parsed = parseVaultPackJson(jsonText, draft, { preserveCurrentPackId: mode === 'edit' });
      setDraft(parsed);
      await onSave(parsed);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'The pack could not be saved.');
    }
  }

  async function handleImport(file: File | null) {
    if (!file) return;
    try {
      const text = await readVaultPackImportFile(file);
      applyJson(text, true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'The file could not be imported.');
    }
  }

  async function handleCopyJsonTemplate() {
    try {
      await navigator.clipboard.writeText(formatVaultPackJson(createVaultPackJsonTemplate()));
      setError('');
      setCopiedTemplate(true);
    } catch {
      setCopiedTemplate(false);
      setError('The JSON format could not be copied.');
    }
  }

  function renderChoiceField(args: {
    fieldKey: string;
    label: string;
    value: string;
    onChange: (value: string) => void;
    options: string[];
    placeholder: string;
    marginBottom?: number;
  }) {
    const path = customFieldPath(args.fieldKey);
    const showCustom = isCustomField(path, args.value, args.options);
    const selectValue = showCustom ? CUSTOM_OPTION : args.value;

    return (
      <div style={{ marginBottom: args.marginBottom }}>
        <Select
          label={args.label}
          value={selectValue}
          onChange={(value) => {
            if (value === CUSTOM_OPTION) {
              updateCustomField(path, true);
              return;
            }
            updateCustomField(path, false);
            args.onChange(value);
          }}
          options={[...args.options, { value: CUSTOM_OPTION, label: 'Custom' }]}
          placeholder={args.placeholder}
          style={FIELD_STYLE}
        />
        {showCustom && (
          <TextField
            label={`${args.label} custom`}
            value={args.value}
            onChange={args.onChange}
            placeholder={`Enter custom ${args.label.toLowerCase()}`}
            style={FIELD_STYLE}
          />
        )}
      </div>
    );
  }

  function renderArrayField(field: ArrayFieldConfig) {
    if (!itemDraft) return null;
    const value = fieldValue(asEditableRecord(itemDraft), field);
    const options = optionValuesFor(field.optionsKey);
    if (field.optionsKey && options.length > 0) {
      return renderChoiceField({
        fieldKey: field.key,
        label: field.label,
        value,
        onChange: (nextValue) => patchArrayDraft(field, nextValue),
        options,
        placeholder: `Choose ${field.label.toLowerCase()}`,
        marginBottom: 10,
      });
    }
    return (
      <TextField
        key={field.key}
        label={field.label}
        value={value}
        onChange={(nextValue) => patchArrayDraft(field, nextValue)}
        readOnly={field.readOnly}
        placeholder={field.placeholder}
        multiline={field.multiline}
        rows={field.rows}
        style={FIELD_STYLE}
        marginBottom={10}
      />
    );
  }

  function renderProgressiveFields() {
    if (!activeSectionKey) {
      return (
        <div
          style={{
            border: `1px dashed ${c.border}`,
            borderRadius: 10,
            padding: '14px 12px',
            background: c.surface1,
          }}
        >
          <p style={{ fontSize: 12, color: c.text2, margin: '0 0 6px' }}>
            Progressive fields complete
          </p>
          <p style={{ fontSize: 11.5, color: c.text3, margin: 0, lineHeight: 1.5 }}>
            Every Vault section has been walked. Review the JSON, then create or save the pack.
          </p>
        </div>
      );
    }

    if (activeSectionKey === 'details') {
      return (
        <>
          <div className="lyx-grid-2" style={{ marginBottom: 10 }}>
            <TextField label="ID" value={draft.id} onChange={() => {}} readOnly style={FIELD_STYLE} />
            <TextField label="Name" value={draft.name} onChange={(name) => patch({ name })} style={FIELD_STYLE} />
          </div>
          <div className="lyx-grid-2" style={{ marginBottom: 10 }}>
            {fieldOptions.categories.length > 0 ? (
              renderChoiceField({
                fieldKey: 'category',
                label: 'Category',
                value: draft.category,
                onChange: (category) => patch({ category }),
                options: fieldOptions.categories,
                placeholder: 'Choose category',
              })
            ) : (
              <TextField
                label="Category"
                value={draft.category}
                onChange={(category) => patch({ category })}
                placeholder="Trending Packs"
                style={FIELD_STYLE}
              />
            )}
            <Select
              label="Visibility"
              value={draft.published === false ? 'unpublished' : 'published'}
              onChange={(value) => patch({ published: value === 'published' })}
              options={[
                { value: 'published', label: 'Published' },
                { value: 'unpublished', label: 'Unpublished' },
              ]}
              style={FIELD_STYLE}
            />
          </div>
          {categories.length > 0 && (
            <p style={{ fontSize: 11, color: c.text3, margin: '0 0 10px' }}>
              Existing categories: {categories.join(' · ')}
            </p>
          )}
          <div className="lyx-grid-2" style={{ marginBottom: 10 }}>
            <TextField
              label="Author"
              value={draft.author}
              onChange={(author) => patch({ author })}
              style={FIELD_STYLE}
            />
            <TextField
              label="Version"
              value={draft.version}
              onChange={() => {}}
              readOnly
              style={FIELD_STYLE}
            />
          </div>
          <div className="lyx-grid-2" style={{ marginBottom: 10 }}>
            <TextField
              label="Release date"
              value={draft.releaseDate}
              onChange={() => {}}
              readOnly
              style={FIELD_STYLE}
            />
            <TextField
              label="Updated"
              value={draft.updated}
              onChange={(updated) => patch({ updated })}
              style={FIELD_STYLE}
            />
          </div>
          <TextField
            label="Description"
            value={draft.description}
            onChange={(description) => patch({ description })}
            multiline
            rows={4}
            style={FIELD_STYLE}
          />
        </>
      );
    }

    if (!activeArraySection) return null;

    return <>{activeArraySection.fields.map((field) => renderArrayField(field))}</>;
  }

  const sectionTitle =
    activeSectionKey === 'details'
      ? 'Pack details'
      : activeArraySection?.title ?? 'Progressive fields';
  const sectionStatus =
    !activeSectionKey
      ? 'Complete'
      : activeSectionKey === 'details'
        ? `Section ${activeSectionIndex + 1} of ${SECTION_KEYS.length}`
        : `${activeArraySection?.singular} ${activeItemIndex + 1} · ${
            editingExistingItem ? 'Existing item' : 'New item'
          }`;

  return (
    <>
      <div className="lyx-drawer-overlay" onClick={onClose} aria-hidden="true" />
      <div
        ref={rootRef}
        className="lyx-drawer"
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
        tabIndex={-1}
        style={{ background: c.surface1, borderLeft: `1px solid ${c.border}` }}
      >
        <div style={{ padding: '18px 20px 32px' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 14,
            }}
          >
            <p
              id={headingId}
              style={{
                fontFamily: 'Space Grotesk',
                fontWeight: 600,
                fontSize: 15,
                margin: 0,
              }}
            >
              {mode === 'create' ? 'Create pack' : 'Edit pack'}
              <span className="lyx-visually-hidden"> — {draft.name || 'Vault pack editor'}</span>
            </p>
            <IconButton label="Close" onClick={onClose}>
              <X size={18} color={c.text3} />
            </IconButton>
          </div>

          <div
            style={{
              border: `1px solid ${c.border}`,
              borderRadius: 12,
              padding: '12px 14px',
              background: c.surface2,
              marginBottom: 14,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <FileText size={14} color={c.text2} />
              <p style={{ fontSize: 12, color: c.text2, margin: 0 }}>Pack JSON import</p>
            </div>
            <p style={{ fontSize: 11.5, color: c.text3, margin: '0 0 10px', lineHeight: 1.5 }}>
              Upload a <strong>.json</strong> file or a <strong>.txt</strong> file containing JSON,
              or paste JSON below.
            </p>
            <input
              ref={fileInputRef}
              type="file"
              accept=".json,.txt,application/json,text/plain"
              className="lyx-visually-hidden"
              aria-label="Upload vault pack JSON"
              onChange={(e) => {
                void handleImport(e.target.files?.item(0) ?? null);
                e.target.value = '';
              }}
            />
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <Btn
                variant="secondary"
                onClick={() => fileInputRef.current?.click()}
                disabled={pending}
              >
                <Upload size={13} />
                Upload JSON / TXT
              </Btn>
              <Btn
                variant="secondary"
                onClick={() => {
                  try {
                    applyJson(jsonText);
                  } catch (err) {
                    setError(
                      err instanceof Error ? err.message : 'The JSON could not be parsed.'
                    );
                  }
                }}
                disabled={pending}
              >
                <FileText size={13} />
                Parse text
              </Btn>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <IconButton
                  label={copiedTemplate ? 'copied' : 'get json format'}
                  onClick={() => void handleCopyJsonTemplate()}
                  disabled={pending}
                >
                  {copiedTemplate ? <Check size={14} color={c.success} /> : <Copy size={14} color={c.text2} />}
                </IconButton>
                {copiedTemplate && (
                  <span style={{ fontSize: 11, color: c.success, fontWeight: 600 }}>Copied</span>
                )}
              </div>
            </div>
          </div>

          <TextField
            label="Pack JSON"
            value={jsonText}
            onChange={(value) => {
              setJsonText(value);
              setError('');
            }}
            multiline
            rows={12}
            style={{ ...FIELD_STYLE, fontFamily: 'IBM Plex Mono', fontSize: 11.5 }}
            marginBottom={14}
          />

          {error && (
            <p role="alert" style={{ fontSize: 12, color: c.danger, margin: '0 0 12px' }}>
              {error}
            </p>
          )}

          <div
            style={{
              border: `1px solid ${c.border}`,
              borderRadius: 12,
              padding: '12px 14px',
              background: c.surface2,
              marginBottom: 14,
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: 12,
                marginBottom: 10,
              }}
            >
              <div>
                <p style={{ fontSize: 12, color: c.text2, margin: '0 0 4px' }}>
                  Progressive fields
                </p>
                <p style={{ fontSize: 11, color: c.text3, margin: 0 }}>{sectionStatus}</p>
              </div>
              <div style={{ textAlign: 'right' }}>
                <p
                  style={{
                    fontSize: 12,
                    color: c.text1,
                    fontWeight: 600,
                    margin: '0 0 4px',
                  }}
                >
                  {sectionTitle}
                </p>
                <p style={{ fontSize: 11, color: c.text3, margin: 0 }}>
                  {activeSectionKey === 'details'
                    ? 'Walk the root pack fields before moving into the array sections.'
                    : activeSectionKey
                      ? 'Next saves this item and opens the next one. Skip saves it too, then ends this array section.'
                      : 'All sections covered.'}
                </p>
              </div>
            </div>

            {renderProgressiveFields()}

            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 6 }}>
              <Btn variant="secondary" onClick={handlePrevious} disabled={pending || !canGoPrevious}>
                Previous
              </Btn>
              {activeSectionKey && (
                <Btn onClick={handleNext} disabled={pending}>
                  Next
                </Btn>
              )}
              {isArraySection(activeSectionKey) && (
                <Btn variant="secondary" onClick={handleSkip} disabled={pending}>
                  Skip
                </Btn>
              )}
              {isArraySection(activeSectionKey) && editingExistingItem && (
                <Btn
                  variant="secondary"
                  onClick={handleRemoveCurrentItem}
                  disabled={pending}
                  style={{ color: c.danger }}
                >
                  Remove current item
                </Btn>
              )}
            </div>
          </div>

          <div className="lyx-grid-2" style={{ marginBottom: 14 }}>
            <div
              style={{
                border: `1px solid ${c.border}`,
                borderRadius: 10,
                padding: '10px 12px',
                background: c.surface2,
              }}
            >
              <p style={{ fontSize: 16, fontWeight: 600, margin: '0 0 2px' }}>{summary.prompts}</p>
              <p style={{ fontSize: 10, color: c.text3, margin: 0, textTransform: 'uppercase' }}>
                Prompts
              </p>
            </div>
            <div
              style={{
                border: `1px solid ${c.border}`,
                borderRadius: 10,
                padding: '10px 12px',
                background: c.surface2,
              }}
            >
              <p style={{ fontSize: 16, fontWeight: 600, margin: '0 0 2px' }}>{summary.presets}</p>
              <p style={{ fontSize: 10, color: c.text3, margin: 0, textTransform: 'uppercase' }}>
                Presets
              </p>
            </div>
            <div
              style={{
                border: `1px solid ${c.border}`,
                borderRadius: 10,
                padding: '10px 12px',
                background: c.surface2,
              }}
            >
              <p style={{ fontSize: 16, fontWeight: 600, margin: '0 0 2px' }}>
                {summary.structures}
              </p>
              <p style={{ fontSize: 10, color: c.text3, margin: 0, textTransform: 'uppercase' }}>
                Structures
              </p>
            </div>
            <div
              style={{
                border: `1px solid ${c.border}`,
                borderRadius: 10,
                padding: '10px 12px',
                background: c.surface2,
              }}
            >
              <p style={{ fontSize: 16, fontWeight: 600, margin: '0 0 2px' }}>{summary.hooks}</p>
              <p style={{ fontSize: 10, color: c.text3, margin: 0, textTransform: 'uppercase' }}>
                Hooks
              </p>
            </div>
            <div
              style={{
                border: `1px solid ${c.border}`,
                borderRadius: 10,
                padding: '10px 12px',
                background: c.surface2,
              }}
            >
              <p style={{ fontSize: 16, fontWeight: 600, margin: '0 0 2px' }}>{summary.metadata}</p>
              <p style={{ fontSize: 10, color: c.text3, margin: 0, textTransform: 'uppercase' }}>
                Metadata
              </p>
            </div>
            <div
              style={{
                border: `1px solid ${c.border}`,
                borderRadius: 10,
                padding: '10px 12px',
                background: c.surface2,
              }}
            >
              <p style={{ fontSize: 16, fontWeight: 600, margin: '0 0 2px' }}>{summary.marketing}</p>
              <p style={{ fontSize: 10, color: c.text3, margin: 0, textTransform: 'uppercase' }}>
                Marketing
              </p>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Btn onClick={() => void handleSave()} disabled={pending}>
              {mode === 'create' ? <Plus size={13} /> : <Save size={13} />}
              {mode === 'create' ? 'Create pack' : 'Save changes'}
            </Btn>
            <Btn variant="secondary" onClick={onClose} disabled={pending}>
              Cancel
            </Btn>
            {mode === 'edit' && draft.id && onDelete && (
              <Btn
                variant="secondary"
                onClick={() => {
                  void onDelete(draft.id).then(onClose);
                }}
                disabled={pending}
                style={{ color: c.danger }}
              >
                <Trash2 size={13} />
                Delete pack
              </Btn>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
